export interface ArchNode {
	id: string
	label: string
	x: number
	y: number
	/** node width override (default 150) for longer labels */
	w?: number
	color: string
	description: string
	tags: string[]
	/** matching skills-matrix category name — selecting the node filters the matrix */
	skillsCategory?: string
}

export interface ArchEdge {
	id: string
	from: string
	to: string
	/** perpendicular curve offset; positive bends one way, negative the other */
	bend?: number
	/** explicit SVG path override for routes a simple curve can't make */
	d?: string
	/** 'data' (solid, animated packets) or 'auth' (dashed identity flow) */
	kind?: 'data' | 'auth'
}

/**
 * Symmetric layout around the center spine:
 * SharePoint (top) → Graph (center) → APIs (lower center) → Entra ID (base).
 * Clients left (SPFx, Teams), compute right (AKS, Functions) — mirrored.
 */
export const architectureContent = {
	title: 'Solution Architecture',
	description: 'Interactive map of the Microsoft 365 stack I build in — hover or tap the nodes',

	nodes: [
		{
			id: 'sharepoint',
			label: 'SharePoint Online',
			x: 360, y: 60,
			color: '#23d18b',
			description: 'Document-centric solutions since 2010 — sites, libraries, provisioning, governance and PowerShell/PnP automation.',
			tags: ['Sites & Libraries', 'PnP provisioning', 'Governance'],
			skillsCategory: 'SharePoint Framework (SPFx)'
		},
		{
			id: 'spfx',
			label: 'SPFx Web Parts',
			x: 130, y: 170,
			color: '#61dafb',
			description: 'Enterprise web parts, field customizers and application customizers in React + TypeScript, shipped through CI/CD since 2019.',
			tags: ['React', 'TypeScript', 'CI/CD'],
			skillsCategory: 'SharePoint Framework (SPFx)'
		},
		{
			id: 'aks',
			label: 'AKS / Kubernetes',
			x: 590, y: 170,
			color: '#9acd32',
			description: '.NET services running in pods on Azure Kubernetes Service — packaged with Helm, managed via Rancher, deployed by Argo CD GitOps pipelines.',
			tags: ['.NET in pods', 'Helm', 'Rancher', 'Argo CD'],
			skillsCategory: 'Infrastructure & DevOps'
		},
		{
			id: 'graph',
			label: 'Microsoft Graph',
			x: 360, y: 230,
			color: '#d7ba7d',
			description: 'The unified API hub — users, sites, files, chats, calendars. My most-used Microsoft API since 2020.',
			tags: ['REST', 'Delegated & app permissions', 'Webhooks'],
			skillsCategory: 'Azure Services'
		},
		{
			id: 'teams',
			label: 'Teams Apps',
			x: 130, y: 340,
			color: '#9a7bff',
			description: 'Custom tab applications and conversational bots with SSO and Microsoft Graph integration.',
			tags: ['Tabs', 'Bots', 'Adaptive Cards'],
			skillsCategory: 'Microsoft Teams Development'
		},
		{
			id: 'functions',
			label: 'Azure Functions',
			x: 590, y: 340,
			color: '#ff7f50',
			description: 'Serverless glue — webhooks, timers and lightweight APIs connecting M365 with other systems. Deployed via Terraform & GitOps.',
			tags: ['Serverless', '.NET / TypeScript', 'Terraform'],
			skillsCategory: 'Azure Services'
		},
		{
			id: 'apis',
			label: 'Internal & External APIs',
			x: 360, y: 405,
			w: 210,
			color: '#febc2e',
			description: 'The integration backbone — REST and SOAP connections to internal line-of-business systems and external partners, with auth, retries and monitoring handled properly.',
			tags: ['REST / SOAP', 'Internal LOB systems', 'External partners'],
			skillsCategory: 'Development Technologies'
		},
		{
			id: 'entra',
			label: 'Entra ID',
			x: 360, y: 485,
			color: '#3b8eea',
			description: 'The identity backbone — SPFx and Teams acquire tokens via SSO/OBO, AKS pods use workload identity, Functions use managed identity, and internal APIs are protected by app registrations. OAuth done right.',
			tags: ['OAuth 2.0', 'SSO / OBO', 'Workload & managed identity', 'App registrations'],
			skillsCategory: 'Azure Services'
		}
	] as ArchNode[],

	edges: [
		// data flows (solid, animated packets)
		{ id: 'spfx-graph', from: 'spfx', to: 'graph', bend: -16, kind: 'data' },
		{ id: 'spfx-sharepoint', from: 'spfx', to: 'sharepoint', bend: -16, kind: 'data' },
		{ id: 'spfx-aks', from: 'spfx', to: 'aks', bend: -24, kind: 'data' },
		// bends route these around the Microsoft Graph node
		{ id: 'spfx-functions', from: 'spfx', to: 'functions', bend: 60, kind: 'data' },
		{ id: 'spfx-apis', from: 'spfx', to: 'apis', bend: -12, kind: 'data' },
		{ id: 'graph-aks', from: 'graph', to: 'aks', bend: -16, kind: 'data' },
		{ id: 'teams-graph', from: 'teams', to: 'graph', bend: 16, kind: 'data' },
		{ id: 'teams-sharepoint', from: 'teams', to: 'sharepoint', bend: -16, kind: 'data' },
		// routed under the Microsoft Graph node
		{ id: 'teams-aks', from: 'teams', to: 'aks', bend: 50, kind: 'data' },
		{ id: 'teams-functions', from: 'teams', to: 'functions', bend: 24, kind: 'data' },
		{ id: 'teams-apis', from: 'teams', to: 'apis', bend: 12, kind: 'data' },
		{ id: 'graph-functions', from: 'graph', to: 'functions', bend: 16, kind: 'data' },
		{ id: 'graph-sharepoint', from: 'graph', to: 'sharepoint', kind: 'data' },
		{ id: 'graph-apis', from: 'graph', to: 'apis', kind: 'data' },
		{ id: 'aks-apis', from: 'aks', to: 'apis', bend: -20, kind: 'data' },
		{ id: 'functions-apis', from: 'functions', to: 'apis', bend: 20, kind: 'data' },

		// identity flows (dashed) — token acquisition, not data
		{ id: 'entra-spfx', from: 'entra', to: 'spfx', bend: -32, kind: 'auth' },
		{ id: 'entra-teams', from: 'entra', to: 'teams', bend: -22, kind: 'auth' },
		{ id: 'entra-aks', from: 'entra', to: 'aks', bend: 32, kind: 'auth' },
		{ id: 'entra-functions', from: 'entra', to: 'functions', bend: 22, kind: 'auth' },
		{ id: 'entra-apis', from: 'entra', to: 'apis', kind: 'auth' },
		// Entra ⇄ Graph: every Graph call carries an Entra-issued token.
		// A direct line would collide with the APIs node, so the route is
		// two mirrored arcs around it — identity wraps the stack.
		{ id: 'entra-graph-left', from: 'entra', to: 'graph', d: 'M 360 485 C 190 460, 190 300, 360 230', kind: 'auth' },
		{ id: 'entra-graph-right', from: 'entra', to: 'graph', d: 'M 360 485 C 530 460, 530 300, 360 230', kind: 'auth' }
	] as ArchEdge[]
}
